<script setup lang="ts">
import { computed } from 'vue';

import { Button, ImageMedia } from '@clovyr/bed';
import Tooltip from '@clovyr/bed/components/blocks/Tooltip.vue';
import { ButtonShape } from '@clovyr/bed/components/controls/types';

import CloseIcon from '@/components/icons/CloseIcon.vue';
import { usePollenStore } from '@/stores/pollen_store';

/**
 *
 * IMPORTANT: moduleId Usage for Dismissible Modules
 * =================================================
 *
 * When using `showClose` prop, you MUST provide a `moduleId` to control dismissal behavior.
 * Use the same moduleId string for modules that should be dismissed together.
 *
 * EXAMPLES:
 * - For global dismissal: <Module show-close module-id="welcome">
 * - For instance-specific: <Module show-close :module-id="deploymentId">
 *
 * Technical Details:
 * - moduleId is stored in localStorage for persistence across sessions
 * - All modules with the same moduleId will be dismissed together
 * - moduleId is REQUIRED when showClose=true (no fallback provided)
 */

const props = defineProps<{
  bigHead?: boolean;
  hed?: string;
  dek?: string;
  img?: string;
  tabs?: string[];
  initialTab?: string;
  showClose?: boolean;
  moduleId?: string; //REQUIRED IF USING showClose
}>();

const emit = defineEmits<{
  close: [];
}>();

const pollenStore = usePollenStore();

// Module visibility - only use store state for modules with showClose prop
const showModule = computed(() => {
  if (props.showClose) {
    // moduleId is required when showClose is true
    if (!props.moduleId) {
      console.error('moduleId is required when using showClose prop');
      return true; // Show the module to prevent it from disappearing
    }
    return pollenStore.isModuleVisible(props.moduleId);
  }
  return true; // Always show modules without showClose prop
});

const handleClose = () => {
  if (props.showClose && props.moduleId) {
    pollenStore.hideModule(props.moduleId);
  }
  emit('close');
};

function getInitialTab() {
  if (!props.tabs) {
    return undefined;
  }

  let tab: string | undefined = undefined;
  const { hash } = window.location;
  if (hash) {
    const s = new URLSearchParams(hash.replace('#', '?'));
    if (s.has('tab')) {
      return s.get('tab')!;
    }
  }
  if (!tab) {
    tab = props.initialTab;
  }

  if (tab && props.tabs.includes(tab)) {
    return tab;
  }

  return props.tabs[0];
}

const activeTab = ref(getInitialTab());

const activateTab = (tab: string) => {
  activeTab.value = tab;
  // set in hash string
  const hash = new URLSearchParams(window.location.hash.replace('#', '?'));
  hash.set('tab', tab);
  window.history.replaceState(null, '', `${window.location.pathname}#${hash.toString()}`);
};
</script>

<template>
  <div
    v-if="showModule"
    class="module"
    :class="{
      'module--bighead': bigHead,
      'module--has-ctas': $slots.ctas,
    }"
  >
    <Button v-if="showClose" :shape="ButtonShape.Circle" @click="handleClose" class="module__close">
      <Tooltip
        content="Hide this welcome message permanently"
        offsetDistance="20"
        placement="top"
        theme="narrow"
      >
        <CloseIcon />
      </Tooltip>
    </Button>
    <div class="module__head" v-if="hed || dek">
      <div class="module__head__grid row">
        <div class="module__head__left grid__col">
          <ImageMedia v-if="img" :filename="img" class="image-media" />
          <!--Use either Hed or Tabs, not both-->
          <div class="module__head__sub" v-if="!tabs">
            <h3 v-if="hed" class="module__head__hed">{{ hed }}</h3>
            <slot name="option"></slot>
          </div>
          <div class="tab-group" v-if="tabs && tabs.length">
            <div
              v-for="tab in tabs"
              :key="tab"
              class="module__head__sub"
              :class="{ active: activeTab === tab }"
            >
              <a @click="activateTab(tab)">
                <h3 class="module__head__hed">{{ tab }}</h3>
              </a>
            </div>
          </div>
          <div v-if="dek" class="module__head__dek">{{ dek }}</div>
        </div>
        <div class="module__head__right grid__col">
          <slot name="heading"></slot>
        </div>
      </div>
    </div>
    <div class="module__body" v-if="$slots.default">
      <slot></slot>
    </div>
    <div class="module__body default2" v-if="$slots.default2">
      <slot name="default2"></slot>
    </div>
    <div class="module__body default3" v-if="$slots.default3">
      <slot name="default3"></slot>
    </div>
    <div class="module__body" v-if="tabs && tabs.length">
      <template v-for="tab in tabs" :key="tab">
        <slot v-if="activeTab === tab" :name="tab.toLowerCase()"></slot>
      </template>
    </div>
    <div class="module__ctas" v-if="$slots.ctas">
      <slot name="ctas"></slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/layout/grid-vars';

.module {
  position: relative;
}

.module__close {
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  background: none;
  border: none;
  cursor: pointer;
  z-index: 10;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0;
}

.image-media {
  height: grid-vars.space(10);
}
.tab-group {
  display: flex;
}
.tab-group > .module__head__sub {
  margin-right: 1rem;
}
.active {
  border-bottom: 1px solid white;
  padding-bottom: 0.5rem;
}
</style>
